/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { STEP_KINDS, type Step, type StepKind } from "../shared/types";

type Props = {
  step: Step;
  index: number;
  count: number;
  onChange: (step: Step) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
};

const KIND_ICON: Record<StepKind, string> = {
  check: "check_box",
  yes_no: "thumbs_up_down",
  temperature: "thermostat",
  number: "pin",
  text: "notes",
};

const num = (v: string) => (v.trim() === "" ? null : Number(v));

// One step of a procedure: what to do, how it's answered, and for readings
// the acceptable range.
export const StepEditorRow = defineComponent<Props>({
  name: "StepEditorRow",
  props: ["step", "index", "count", "onChange", "onMove", "onRemove"],
  setup(props) {
    const { t } = useLocalization();
    const set = (patch: Partial<Step>) => props.onChange({ ...props.step, ...patch });

    return () => {
      const s = props.step;
      const ranged = s.kind === "temperature" || s.kind === "number";
      return (
        <div class="rounded-lg border border-base-300 bg-base-100 p-3">
          <div class="flex items-start gap-2">
            <span class="mt-2 w-5 text-right text-xs text-base-content/40">{props.index + 1}</span>
            <div class="flex-1 space-y-2">
              <input
                class="input input-bordered input-sm w-full"
                placeholder={t("workday.procedures.step_placeholder")}
                value={s.title}
                onInput={(e: Event) => set({ title: (e.target as HTMLInputElement).value })}
              />
              <div class="flex flex-wrap items-center gap-2">
                <div class="join">
                  {STEP_KINDS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      title={t(`workday.step_kind.${k}`)}
                      class={["btn join-item btn-xs gap-1", s.kind === k ? "btn-primary" : "btn-ghost border-base-300"].join(" ")}
                      onClick={() =>
                        set({
                          kind: k,
                          unit: k === "temperature" ? (s.unit ?? "°C") : k === "number" ? s.unit : null,
                          min_value: k === "temperature" || k === "number" ? s.min_value : null,
                          max_value: k === "temperature" || k === "number" ? s.max_value : null,
                        })
                      }
                    >
                      <span class="material-symbols-outlined text-sm">{KIND_ICON[k]}</span>
                      <span class="hidden sm:inline">{t(`workday.step_kind.${k}`)}</span>
                    </button>
                  ))}
                </div>
                {ranged && (
                  <div class="flex items-center gap-1 text-xs">
                    <span class="text-base-content/60">{t("workday.procedures.range")}</span>
                    <input
                      type="number"
                      step="any"
                      class="input input-bordered input-xs w-16 text-right"
                      placeholder={t("workday.procedures.min")}
                      value={s.min_value ?? ""}
                      onChange={(e: Event) => set({ min_value: num((e.target as HTMLInputElement).value) })}
                    />
                    <span>–</span>
                    <input
                      type="number"
                      step="any"
                      class="input input-bordered input-xs w-16 text-right"
                      placeholder={t("workday.procedures.max")}
                      value={s.max_value ?? ""}
                      onChange={(e: Event) => set({ max_value: num((e.target as HTMLInputElement).value) })}
                    />
                    <input
                      class="input input-bordered input-xs w-14"
                      placeholder={t("workday.procedures.unit")}
                      value={s.unit ?? ""}
                      onInput={(e: Event) => set({ unit: (e.target as HTMLInputElement).value || null })}
                    />
                  </div>
                )}
                <label class="ml-auto flex cursor-pointer items-center gap-1 text-xs text-base-content/70">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-xs"
                    checked={s.required}
                    onChange={(e: Event) => set({ required: (e.target as HTMLInputElement).checked })}
                  />
                  {t("workday.procedures.required")}
                </label>
              </div>
            </div>
            <div class="flex flex-col">
              <button class="btn btn-ghost btn-xs btn-square" disabled={props.index === 0} onClick={() => props.onMove(-1)} aria-label={t("workday.common.move_up")}>
                <span class="material-symbols-outlined text-base">keyboard_arrow_up</span>
              </button>
              <button class="btn btn-ghost btn-xs btn-square" disabled={props.index === props.count - 1} onClick={() => props.onMove(1)} aria-label={t("workday.common.move_down")}>
                <span class="material-symbols-outlined text-base">keyboard_arrow_down</span>
              </button>
            </div>
            <button class="btn btn-ghost btn-xs btn-square" onClick={() => props.onRemove()} aria-label={t("workday.common.remove")}>
              <span class="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        </div>
      );
    };
  },
});
